import { PATIENT_SERVICES } from '@avanta/shared/constants/patients';
import { UserType } from '@avanta/shared/constants/users';
import type { User } from '@avanta/shared/types/users';
import { Brain, Calendar, Home, MessageCircle, HeartPulse } from 'lucide-react';
import { ROUTES } from './routes';

export interface NavRoute {
  href: string;
  label: string;
  icon: React.ElementType;
}

const HOME_NAV_ROUTE: NavRoute = { href: ROUTES.HOME, label: 'start', icon: Home };
const PAE_NAV_ROUTE: NavRoute = { href: ROUTES.PAE, label: 'pae', icon: Home };
const SAPA_NAV_ROUTE: NavRoute = { href: ROUTES.SAPA, label: 'sapa', icon: Home };

const COMMON_NAV_ROUTES: NavRoute[] = [
  { href: ROUTES.APPOINTMENTS, label: 'appointments', icon: Calendar },
  { href: ROUTES.WELL_BEING, label: 'wellBeing', icon: Brain },
];

const EMPLOYEE_NAV_ROUTES: NavRoute[] = [PAE_NAV_ROUTE, ...COMMON_NAV_ROUTES];
const PATIENT_NAV_ROUTES: NavRoute[] = [HOME_NAV_ROUTE, ...COMMON_NAV_ROUTES];

const PSYCHOLOGIST_NAV_ROUTES: NavRoute[] = [
  HOME_NAV_ROUTE,
  { href: ROUTES.CHAT, label: 'chat', icon: MessageCircle },
  { href: ROUTES.PSYCHOLOGIST_PATIENTS, label: 'psychologistPatients', icon: HeartPulse },
];

const SUPPORTER_NAV_ROUTES: NavRoute[] = [
  { href: ROUTES.CHAT, label: 'chat', icon: MessageCircle },
];

type UserTypeRoutes =
  UserType.PATIENT | UserType.EMPLOYEE | UserType.SUPPORTER | UserType.PSYCHOLOGIST;

const NAV_ROUTES_BY_USER_TYPE: Record<UserTypeRoutes, NavRoute[]> = {
  [UserType.PATIENT]: PATIENT_NAV_ROUTES,
  [UserType.EMPLOYEE]: EMPLOYEE_NAV_ROUTES,
  [UserType.SUPPORTER]: SUPPORTER_NAV_ROUTES,
  [UserType.PSYCHOLOGIST]: PSYCHOLOGIST_NAV_ROUTES,
};

type PatientService = (typeof PATIENT_SERVICES)[keyof typeof PATIENT_SERVICES];

const SERVICE_AWARE_USER_TYPES = new Set<UserType>([UserType.PATIENT, UserType.EMPLOYEE]);

const SERVICE_NAV_ROUTES: ReadonlyArray<[PatientService, NavRoute]> = [
  [PATIENT_SERVICES.PRIVATE, HOME_NAV_ROUTE],
  [PATIENT_SERVICES.PAE, PAE_NAV_ROUTE],
  [PATIENT_SERVICES.SAPA, SAPA_NAV_ROUTE],
];

const sameRoutes = (routes: NavRoute[], otherRoutes: NavRoute[]): boolean =>
  routes.length === otherRoutes.length &&
  routes.every((route, index) => route === otherRoutes[index]);

export const pathToNavKey = (path: string): string =>
  path
    .replace(/^\//, '')
    .split(/[-/]/)
    .map((segment, index) =>
      index === 0 ? segment : segment.charAt(0).toUpperCase() + segment.slice(1)
    )
    .join('');

export const getRoutes = (user: User | null): NavRoute[] => {
  const userType = user?.type as UserTypeRoutes;
  const defaultRoutes = userType ? NAV_ROUTES_BY_USER_TYPE[userType] : PATIENT_NAV_ROUTES;

  if (!userType || !SERVICE_AWARE_USER_TYPES.has(userType)) {
    return defaultRoutes;
  }

  const services = user?.activeServices ?? [];
  const serviceRoutes: NavRoute[] = [];

  for (const [service, route] of SERVICE_NAV_ROUTES) {
    if (services.includes(service)) serviceRoutes.push(route);
  }

  const routes =
    serviceRoutes.length === 0 ? defaultRoutes : [...serviceRoutes, ...COMMON_NAV_ROUTES];

  const result = sameRoutes(routes, defaultRoutes) ? defaultRoutes : routes;

  return user?.canBookAppointments
    ? result
    : result.filter(route => route.href !== ROUTES.APPOINTMENTS);
};
